import React, { useEffect, useState } from "react";
import { api, assetUrl } from "../lib/api";
import { toast } from "sonner";
import { Trash2, Check, AlertTriangle, Flag, Image as ImageIcon } from "lucide-react";

export const ArnakAdsModerationPanel = () => {
  const [data, setData] = useState({ ads: [], stats: { total: 0, pending: 0, published: 0, reported: 0 } });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all"); // all | pending | reported | published

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/admin/arnak/ads"); setData(data); }
    catch (e) { toast.error("Erreur"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const moderate = async (adId, action) => {
    if (action === "delete" && !window.confirm("Supprimer cette annonce définitivement ?")) return;
    try { await api.post(`/admin/arnak/ads/${adId}`, { action }); toast.success(action === "delete" ? "Supprimée" : "Validée"); load(); }
    catch (e) { toast.error("Erreur"); }
  };

  const ads = data.ads.filter((a) => {
    if (filter === "pending")   return a.status === "pending";
    if (filter === "reported")  return a.reports > 0;
    if (filter === "published") return a.status === "published";
    return true;
  });

  return (
    <div className="space-y-5" data-testid="arnak-ads-mod-panel">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <p className="text-[10px] uppercase tracking-[0.3em] text-yellow-400 font-bold">Modération marketplace</p>
          <h2 className="font-black text-2xl mt-1">Annonces ARNAK</h2>
        </div>
        <div className="flex gap-2 flex-wrap">
          {[
            { key: "all",       label: `Tout (${data.stats.total})` },
            { key: "pending",   label: `⏳ En attente (${data.stats.pending})`,  highlight: data.stats.pending > 0 },
            { key: "reported",  label: `🚩 Signalées (${data.stats.reported})`,   highlight: data.stats.reported > 0 },
            { key: "published", label: `✅ Publiées (${data.stats.published})` },
          ].map((f) => (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              className={`px-3 py-1.5 text-[11px] font-black uppercase tracking-widest rounded-lg transition ${filter === f.key ? "bg-yellow-400 text-black" : f.highlight ? "bg-orange-500/20 text-orange-300 border border-orange-400/40 hover:bg-orange-500/30" : "bg-white/[0.04] border border-white/10 text-white/55 hover:text-white"}`}
              data-testid={`ads-filter-${f.key}`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>
      {loading ? (
        <p className="text-center py-12 text-white/40 text-sm">Chargement…</p>
      ) : ads.length === 0 ? (
        <p className="text-center py-12 text-white/40 text-sm">Aucune annonce dans ce filtre.</p>
      ) : (
        <div className="space-y-3">
          {ads.map((ad) => (
            <div key={ad.ad_id} className="bg-white/[0.03] border border-white/10 rounded-lg p-4 flex flex-col sm:flex-row gap-4" data-testid={`mod-ad-${ad.ad_id}`}>
              <div className="w-full sm:w-32 h-32 rounded-lg overflow-hidden bg-zinc-900 shrink-0">
                {ad.photos?.[0] ? (
                  <img src={assetUrl(ad.photos[0])} alt={ad.title} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-white/20"><ImageIcon size={28} /></div>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                  {ad.status === "pending" && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] uppercase tracking-widest font-black bg-orange-500/20 text-orange-300 rounded">
                      <AlertTriangle size={10} /> En attente · {ad.moderation_flag || "auto"}
                    </span>
                  )}
                  {ad.reports > 0 && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] uppercase tracking-widest font-black bg-red-500/20 text-red-300 rounded">
                      <Flag size={10} /> {ad.reports} signalement{ad.reports > 1 ? "s" : ""}
                    </span>
                  )}
                  <span className="text-[10px] uppercase tracking-widest font-bold text-white/45">{ad.category}</span>
                </div>
                <p className="font-bold text-base truncate">{ad.title}</p>
                <p className="text-yellow-400 font-black text-sm mt-0.5">
                  {ad.price_type === "fixed" ? `${ad.price_amount || 0} Ar` : ad.price_type === "free" ? "Gratuit" : ad.price_type === "negotiable" ? "À débattre" : "Échange"}
                </p>
                <p className="text-white/55 text-xs mt-1 line-clamp-2">{ad.description}</p>
                <p className="text-[10px] text-white/40 mt-2">
                  Par <strong className="text-white/65">{ad.author_name}</strong> ({ad.author_email}) · {ad.city || "Pas de ville"}
                </p>
              </div>
              <div className="flex sm:flex-col gap-2 shrink-0">
                {ad.status === "pending" && (
                  <button onClick={() => moderate(ad.ad_id, "approve")} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-green-500/15 hover:bg-green-500/30 border border-green-400/40 text-green-300 text-[11px] uppercase tracking-widest font-black transition" data-testid={`approve-${ad.ad_id}`}>
                    <Check size={12} /> Valider
                  </button>
                )}
                <a href={`/annonces/${ad.ad_id}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-white/5 hover:bg-white/15 text-[11px] uppercase tracking-widest font-black transition text-center">
                  Voir
                </a>
                <button onClick={() => moderate(ad.ad_id, "delete")} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-red-500/15 hover:bg-red-500/30 border border-red-400/40 text-red-300 text-[11px] uppercase tracking-widest font-black transition" data-testid={`delete-${ad.ad_id}`}>
                  <Trash2 size={12} /> Suppr.
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ArnakAdsModerationPanel;
